// src/components/client/AuthSessionProvider.tsx
"use client";

import { useEffect } from "react";
import { SessionProvider, useSession } from "next-auth/react";
import type { Session } from "next-auth";



// Sync the user's timezone with the server on initial load.
const TimeZoneSync = () => {
  const { status } = useSession();

  useEffect(() => {
    if (status !== "authenticated") return;

    const browserTimeZone = Intl.DateTimeFormat().resolvedOptions().timeZone?.trim();
    if (!browserTimeZone) return;

    const controller = new AbortController();

    const syncTimeZone = async () => {
      try {
        const profileResponse = await fetch("/api/user", {
          cache: "no-store",
          signal: controller.signal,
        });
        const profile = (await profileResponse.json().catch(() => null)) as
          | { user?: { userTimeZone?: string | null } }
          | null;

        if (!profileResponse.ok) return;

        const storedTimeZone = profile?.user?.userTimeZone?.trim() ?? "";
        if (storedTimeZone && storedTimeZone !== "UTC") return;
        if (storedTimeZone === browserTimeZone) return;

        await fetch("/api/user", {
          method: "PUT",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ userTimeZone: browserTimeZone }),
          signal: controller.signal,
        });
      } catch (error) {
        if ((error as Error).name === "AbortError") return;
        console.error("[TIMEZONE_SYNC]", error);
      }
    };

    void syncTimeZone();

    return () => {
      controller.abort();
    };
  }, [status]);

  return null;
};

export function AuthSessionProvider({
  children,
  session,
}: {
  children: React.ReactNode;
  session: Session | null;
}) {
  return (
    <SessionProvider session={session} basePath="/api/auth" >
      <TimeZoneSync />
      {children}
    </SessionProvider>
  );
}
